import { useState } from 'react'
import { useSettingsStore } from '@/stores/settings'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Switch } from '@/components/ui/switch'
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Separator } from '@/components/ui/separator'
import { Badge } from '@/components/ui/badge'
import { 
  Bell, 
  Mail, 
  Smartphone, 
  Clock, 
  Volume2,
  VolumeX,
  Save,
  RefreshCw
} from 'lucide-react'
import { toast } from 'sonner'

export function NotificationSettings() {
  const { notifications, updateNotifications, loading } = useSettingsStore()
  const [hasChanges, setHasChanges] = useState(false)
  const [formData, setFormData] = useState(notifications)

  const handleToggleEmailNotification = (key: keyof typeof notifications.email_notifications) => {
    const newData = {
      ...formData,
      email_notifications: {
        ...formData.email_notifications,
        [key]: !formData.email_notifications[key]
      }
    }
    setFormData(newData)
    setHasChanges(true)
  }

  const handleTogglePushNotification = (key: keyof typeof notifications.push_notifications) => {
    const newData = {
      ...formData,
      push_notifications: {
        ...formData.push_notifications,
        [key]: !formData.push_notifications[key]
      }
    }
    setFormData(newData)
    setHasChanges(true)
  }

  const handleAlertFrequencyChange = (frequency: 'immediate' | 'hourly' | 'daily') => {
    const newData = {
      ...formData,
      alert_frequency: frequency
    }
    setFormData(newData)
    setHasChanges(true)
  }

  const handleQuietHoursChange = (field: string, value: boolean | string) => {
    const newData = {
      ...formData,
      quiet_hours: {
        ...formData.quiet_hours,
        [field]: value
      }
    }
    setFormData(newData)
    setHasChanges(true)
  }

  const handleSave = async () => {
    try {
      await updateNotifications(formData)
      setHasChanges(false)
      toast.success('Notification settings saved!')
    } catch (error) {
      toast.error('Failed to save settings')
    }
  }

  const handleReset = () => {
    setFormData(notifications)
    setHasChanges(false)
  }

  const emailNotificationOptions = [
    {
      key: 'security_alerts' as const,
      label: 'Security Alerts',
      description: 'Important security notifications and login attempts'
    },
    {
      key: 'server_alerts' as const,
      label: 'Server Alerts',
      description: 'Server downtime, errors, and performance issues'
    },
    {
      key: 'team_invitations' as const,
      label: 'Team Invitations',
      description: 'Invitations to join teams and role changes'
    },
    {
      key: 'weekly_digest' as const,
      label: 'Weekly Digest',
      description: 'Weekly summary of your activity and statistics'
    },
    {
      key: 'marketing_updates' as const,
      label: 'Marketing Updates',
      description: 'Product updates, feature announcements, and newsletters'
    }
  ]

  const pushNotificationOptions = [
    {
      key: 'server_down' as const,
      label: 'Server Down',
      description: 'Immediate alerts when servers go offline'
    },
    {
      key: 'high_error_rate' as const,
      label: 'High Error Rate',
      description: 'Alerts when error rates exceed thresholds'
    },
    {
      key: 'deployment_success' as const,
      label: 'Deployment Success',
      description: 'Notifications when deployments complete successfully'
    },
    {
      key: 'team_mentions' as const,
      label: 'Team Mentions',
      description: 'When you are mentioned in team discussions'
    }
  ]

  return (
    <div className="space-y-6">
      {/* Email Notifications */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Mail className="h-5 w-5" />
            <span>Email Notifications</span>
          </CardTitle>
          <CardDescription>
            Choose which emails you'd like to receive
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {emailNotificationOptions.map((option) => (
              <div key={option.key} className="flex items-center justify-between p-4 border rounded-lg">
                <div className="flex-1">
                  <div className="font-medium">{option.label}</div>
                  <div className="text-sm text-muted-foreground">
                    {option.description}
                  </div>
                </div>
                <Switch
                  checked={formData.email_notifications[option.key]}
                  onCheckedChange={() => handleToggleEmailNotification(option.key)}
                />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Push Notifications */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Smartphone className="h-5 w-5" />
            <span>Push Notifications</span>
          </CardTitle>
          <CardDescription>
            Get instant notifications on your devices
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {pushNotificationOptions.map((option) => (
              <div key={option.key} className="flex items-center justify-between p-4 border rounded-lg">
                <div className="flex-1">
                  <div className="font-medium">{option.label}</div>
                  <div className="text-sm text-muted-foreground">
                    {option.description}
                  </div>
                </div>
                <Switch
                  checked={formData.push_notifications[option.key]}
                  onCheckedChange={() => handleTogglePushNotification(option.key)}
                />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Alert Frequency */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Bell className="h-5 w-5" />
            <span>Alert Frequency</span>
          </CardTitle>
          <CardDescription>
            How often you'd like to receive alert notifications
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Notification Frequency</Label>
              <Select value={formData.alert_frequency} onValueChange={handleAlertFrequencyChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Select frequency" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="immediate">Immediate</SelectItem>
                  <SelectItem value="hourly">Hourly Digest</SelectItem>
                  <SelectItem value="daily">Daily Summary</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-sm text-muted-foreground">
                {formData.alert_frequency === 'immediate' && 'Receive alerts as soon as they occur'}
                {formData.alert_frequency === 'hourly' && 'Receive a summary of alerts every hour'}
                {formData.alert_frequency === 'daily' && 'Receive a daily summary of all alerts'}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Quiet Hours */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Clock className="h-5 w-5" />
            <span>Quiet Hours</span>
          </CardTitle>
          <CardDescription>
            Set times when you don't want to receive notifications
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div className="flex items-center space-x-3">
                {formData.quiet_hours.enabled ? (
                  <VolumeX className="h-5 w-5 text-muted-foreground" />
                ) : (
                  <Volume2 className="h-5 w-5 text-muted-foreground" />
                )}
                <div>
                  <div className="font-medium">Enable Quiet Hours</div>
                  <div className="text-sm text-muted-foreground">
                    {formData.quiet_hours.enabled 
                      ? `Active from ${formData.quiet_hours.start} to ${formData.quiet_hours.end}`
                      : 'Notifications will be sent at all times'
                    }
                  </div>
                </div>
              </div>
              <Switch
                checked={formData.quiet_hours.enabled}
                onCheckedChange={(checked) => handleQuietHoursChange('enabled', checked)}
              />
            </div>

            {formData.quiet_hours.enabled && (
              <div className="grid grid-cols-2 gap-4 p-4 border rounded-lg bg-muted/50">
                <div className="space-y-2">
                  <Label htmlFor="quiet-start">Start Time</Label>
                  <Input
                    id="quiet-start"
                    type="time"
                    value={formData.quiet_hours.start}
                    onChange={(e) => handleQuietHoursChange('start', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="quiet-end">End Time</Label>
                  <Input
                    id="quiet-end"
                    type="time"
                    value={formData.quiet_hours.end}
                    onChange={(e) => handleQuietHoursChange('end', e.target.value)}
                  />
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Notification Summary */}
      <Card>
        <CardHeader>
          <CardTitle>Notification Summary</CardTitle>
          <CardDescription>
            Overview of your current notification settings
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 border rounded-lg">
                <div className="text-2xl font-bold text-blue-600">
                  {Object.values(formData.email_notifications).filter(Boolean).length}
                </div>
                <div className="text-sm text-muted-foreground">Email types enabled</div>
              </div>
              <div className="p-4 border rounded-lg">
                <div className="text-2xl font-bold text-green-600">
                  {Object.values(formData.push_notifications).filter(Boolean).length}
                </div>
                <div className="text-sm text-muted-foreground">Push types enabled</div>
              </div>
            </div>

            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div>
                <div className="font-medium">Alert Frequency</div>
                <div className="text-sm text-muted-foreground">
                  {formData.alert_frequency.charAt(0).toUpperCase() + formData.alert_frequency.slice(1)}
                </div>
              </div>
              <Badge variant="outline">
                {formData.alert_frequency}
              </Badge>
            </div>

            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div>
                <div className="font-medium">Quiet Hours</div>
                <div className="text-sm text-muted-foreground">
                  {formData.quiet_hours.enabled 
                    ? `${formData.quiet_hours.start} - ${formData.quiet_hours.end}`
                    : 'Disabled'
                  }
                </div>
              </div>
              <Badge variant={formData.quiet_hours.enabled ? 'default' : 'outline'}>
                {formData.quiet_hours.enabled ? 'Active' : 'Disabled'}
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Save Changes */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          {hasChanges && (
            <Badge variant="outline" className="text-orange-600">
              Unsaved changes
            </Badge>
          )}
        </div>
        
        <div className="flex items-center space-x-2">
          {hasChanges && (
            <Button variant="outline" onClick={handleReset}>
              Reset
            </Button>
          )}
          <Button 
            onClick={handleSave} 
            disabled={!hasChanges || loading}
            className="flex items-center space-x-2"
          >
            {loading ? (
              <RefreshCw className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            <span>{loading ? 'Saving...' : 'Save Changes'}</span>
          </Button>
        </div>
      </div>
    </div>
  )
} 